import React, { createContext, useContext, useState } from "react";
import { Box, Button, Modal, TextField, Typography } from "@mui/material";
import { axiosClient } from "../api/proxy/AxiosClient";

const PaymentContext = createContext(null);

const modalStyle = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: 420,
    bgcolor: "background.paper",
    border: "2px solid #000",
    boxShadow: 24,
    borderRadius: 2,
    p: 3,
};

export const PaymentProvider = ({ children }) => {
  
    const [open, setOpen] = useState(false);
    const [student, setStudent] = useState(null);
    const [teacher, setTeacher] = useState(null);
    const [lesson, setLesson] = useState(null);
    const [amount, setAmount] = useState("");
    const [ccy, setCcy] = useState("");
    const [comment, setComment] = useState("");
    const [discount, setDiscount] = useState("");
    const [loading, setLoading] = useState(false);
    const [onOperationSuccessRef, setOnOperationSuccessRef] = useState(null);
    const [mode, setMode] = useState(null);
    const [studentBalances, setStudentBalances] = useState({});

    const openPay = (student, lesson, onOperationSuccess = () => { }) => {
        setMode('pay');
        setStudent(student);
        setLesson(lesson);
        setAmount("");
        setDiscount("");
        setOpen(true);
        setOnOperationSuccessRef(() => onOperationSuccess);
    };

    const openMakeDeposit = (teacher, student, studentBalances, defaultCcy = "", onOperationSuccess = () => { }) => {
        setMode('deposit');
        setTeacher(teacher);
        setStudent(student);
        setAmount("");
        setCcy(defaultCcy);
        setComment("");
        setOpen(true);
        setStudentBalances(studentBalances);
        setOnOperationSuccessRef(() => onOperationSuccess);
    };

    const handleClose = () => {
        setOpen(false);
        setMode(null);
    };

    const handleSubmitPayment = async () => {
        if (!student || !lesson) return;
        setLoading(true);
        try {
            const dto = {
                studentUid: student.uid,
                lessonUid: lesson.uid,
                amount: parseFloat(amount || 0),
                ccy: lesson.ccy,
                comment: discount ? `Discount applied: ${discount}` : undefined,
            };
            const response = await axiosClient.post("/payments/lesson", dto);
            handleClose();
            onOperationSuccessRef(response.data);
        } catch (err) {
            console.error("Payment error", err);
        } finally {
            setLoading(false);
        }
    };

    const handleSubmitDeposit = async () => {
        if (!student || !teacher) return;
        setLoading(true);
        try {
            const dto = {
                debtorUid: student.uid,
                creditorUid: teacher.uid,
                amount: parseFloat(amount || 0),
                ccy: ccy.toUpperCase().slice(0, 4),
                comment: comment || undefined,
            };
            const response = await axiosClient.post("/payments", dto);
            handleClose();
            onOperationSuccessRef(response.data);
        } catch (err) {
            console.error("Deposit error", err);
        } finally {
            setLoading(false);
        }
    };

    return (
        <PaymentContext.Provider value={{ openPay, openMakeDeposit }}>
            {children}
            <Modal
                open={open}
                onClose={handleClose}
                aria-labelledby="payment-modal-title"
            >
                <Box sx={modalStyle}>
                    <Typography id="payment-modal-title" variant="h6" mb={2}>
                        {teacher
                            ? `Make Deposit for Teacher ${teacher.firstName} ${teacher.lastName} on behalf of Student ${student?.name}`
                            : `Make Payment for Lesson "${lesson?.name}" on behalf of ${student?.name}`}
                    </Typography>

                    {/* Вводимые поля */}
                    <TextField
                        fullWidth
                        margin="normal"
                        label="Amount"
                        type="number"
                        value={amount}
                        onChange={(e) => setAmount(e.target.value)}
                    />

                    {mode === 'deposit' && (
                        <>
                            <Box display="flex" alignItems="center" gap={1} mt={2} mb={2}>
                                <TextField
                                    label="Currency"
                                    value={ccy}
                                    onChange={(e) =>
                                        setCcy(e.target.value.toUpperCase().slice(0, 4))
                                    }
                                    slotProps={{ input: { maxLength: 4 } }}
                                    sx={{ flexGrow: 1 }}
                                />
                                {/* Кнопки для валют из studentBalances */}
                                {studentBalances &&
                                    Object.entries(studentBalances).map(([ccy, amount]) => (
                                        <Button
                                            key={ccy}
                                            variant="outlined"
                                            size="small"
                                            onClick={() => setCcy(ccy)}
                                        >
                                            {ccy} ({amount})
                                        </Button>
                                    ))}
                            </Box>
                        </>
                    )}

                    {mode === 'pay' && (
                        <>
                            <TextField
                                fullWidth
                                margin="normal"
                                label="Discount"
                                value={discount}
                                onChange={(e) => setDiscount(e.target.value)}
                            />
                            <TextField
                                fullWidth
                                margin="normal"
                                label="Currency"
                                value={lesson?.ccy || ""}
                                slotProps={{ input: { readOnly: true } }}
                                sx={{ bgcolor: "#f0f0f0", "& fieldset": { borderColor: "#9e9e9e" } }}
                            />
                            <TextField
                                fullWidth
                                margin="normal"
                                label="Student Fee"
                                value={student?.fee || ""}
                                slotProps={{ input: { readOnly: true } }}
                                sx={{ bgcolor: "#f0f0f0", "& fieldset": { borderColor: "#9e9e9e" } }}
                            />
                            <TextField
                                fullWidth
                                margin="normal"
                                label="Lesson Balance"
                                value={student?.lessonBalance || ""}
                                slotProps={{ input: { readOnly: true } }}
                                sx={{
                                    bgcolor: "#f0f0f0",
                                    "& fieldset": { borderColor: "#9e9e9e" },
                                }}
                            />
                            <TextField
                                fullWidth
                                margin="normal"
                                label="Already Paid"
                                value={student?.paid || ""}
                                slotProps={{ input: { readOnly: true } }}
                                sx={{
                                    bgcolor: "#f0f0f0",
                                    "& fieldset": { borderColor: "#9e9e9e" },
                                }}
                            />
                        </>
                    )}

                    <TextField
                        fullWidth
                        margin="normal"
                        label="Comment"
                        value={comment}
                        onChange={(e) => setComment(e.target.value)}
                    />

                    <TextField
                        fullWidth
                        margin="normal"
                        label="Student Deposit"
                        value={student?.deposit || ""}
                        slotProps={{ input: { readOnly: true } }}
                        sx={{
                            bgcolor: "#f0f0f0",
                            "& fieldset": { borderColor: "#9e9e9e" },
                        }}
                    />

                    {/* Кнопки */}
                    <Box mt={3} display="flex" justifyContent="flex-end" gap={2}>
                        <Button onClick={handleClose} variant="outlined">
                            Cancel
                        </Button>
                        <Button
                            onClick={() => {
                                mode === 'pay' ? handleSubmitPayment() :
                                    mode === 'deposit' ? handleSubmitDeposit() :
                                        (() => { })();
                            }}
                            variant="contained"
                            disabled={loading || !amount}
                        >
                            {loading ? "Processing..." : "Submit"}
                        </Button>
                    </Box>
                </Box>
            </Modal>
        </PaymentContext.Provider>
    );
};

// Хук для доступа к сервису
export const usePaymentService = () => useContext(PaymentContext);
